import { $pegApi } from '@/boot/peg-api'
import { useCustomerReturnErrorHandler } from '@/composables/useCustomerReturnErrorHandler'

/**
 * Customer Return Retailer Store
 *
 * Manages retailer lookup, selection, and state for the customer return flow.
 * Handles multiple retailer scenarios and retailer data management.
 *
 * @public API:
 * - State: retailer, retailerId, selectedRetailer, retailersFound, showRetailerSelection, loading
 * - Computed: hasMultipleRetailers, activeRetailer
 * - Actions: lookupByOrder, setRetailer, setRetailersFound, selectRetailer, clearSelection, reset
 */

/**
 * @typedef {Object} Retailer
 * @property {string} id - Retailer ID
 * @property {string} name - Retailer name
 * @property {string} [logo] - Retailer logo URL
 * @property {Object} [returnPolicy] - Retailer return policy
 */

// ============================================================================
// PRIVATE HELPER FUNCTIONS
// ============================================================================

/**
 * Validate retailer data structure
 * @param {any} retailerData - Data to validate
 * @returns {boolean} True if valid
 * @throws {Error} If validation fails
 * @private
 */
function _validateRetailerData(retailerData) {
  if (!retailerData || !retailerData.id) {
    throw new Error('Invalid retailer data: missing id')
  }
  return true
}

/**
 * Validate retailers array
 * @param {any} retailers - Data to validate
 * @returns {boolean} True if valid
 * @throws {Error} If validation fails
 * @private
 */
function _validateRetailersArray(retailers) {
  if (!Array.isArray(retailers)) {
    throw new Error('Retailers must be an array')
  }
  return true
}

// ============================================================================
// STORE DEFINITION
// ============================================================================

export const useCustomerReturnRetailerStore = defineStore('customerReturnRetailer', () => {
  // composables
  const { handleRetailerLookupError } = useCustomerReturnErrorHandler()

  // ============================================================================
  // STATE
  // ============================================================================

  /**
   * Main retailer data
   * @type {import('vue').Ref<Retailer|null>}
   * @public
   */
  const retailer = ref(null)

  /**
   * Current retailer ID
   * @type {import('vue').Ref<string|null>}
   * @public
   */
  const retailerId = ref(null)

  /**
   * Selected retailer in multi-retailer scenario
   * @type {import('vue').Ref<Retailer|null>}
   * @public
   */
  const selectedRetailer = ref(null)

  /**
   * List of retailers found during lookup
   * @type {import('vue').Ref<Retailer[]>}
   * @public
   */
  const retailersFound = ref([])

  /**
   * Whether to show retailer selection UI
   * @type {import('vue').Ref<boolean>}
   * @public
   */
  const showRetailerSelection = ref(false)

  /**
   * Loading state for async operations
   * @type {import('vue').Ref<boolean>}
   * @public
   */
  const loading = ref(false)

  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================

  /**
   * Check if multiple retailers were found
   * @type {import('vue').ComputedRef<boolean>}
   * @public
   */
  const hasMultipleRetailers = computed(() => retailersFound.value.length > 1)

  /**
   * Get the current active retailer (selected or main)
   * @type {import('vue').ComputedRef<Retailer|null>}
   * @public
   */
  const activeRetailer = computed(() => selectedRetailer.value || retailer.value)

  // ============================================================================
  // ACTIONS (PUBLIC API)
  // ============================================================================

  /**
   * Lookup retailer by email and order number
   * @param {Object} params
   * @param {string} params.email - Customer email address
   * @param {string} params.orderNumber - Order number
   * @param {string} [params.giftGiverEmail] - Gift giver email for gift returns
   * @returns {Promise<Object>} Object with retailers array, isMultiple flag, and orderLookupToken
   * @public
   */
  async function lookupByOrder({ email, orderNumber, giftGiverEmail = null }) {
    try {
      loading.value = true
      let retailers = []
      const {
        retailers: retailerData,
        id: orderLookupToken,
        message,
        statusCode,
      } = await $pegApi.Retailers.OrderLookup.getSingle(email, orderNumber, {
        giftGiverEmail,
      })

      if (retailerData && retailerData.length > 0) {
        retailers = retailerData
      }

      // If multiple retailers found, set them for selection
      if (retailers.length > 1) {
        setRetailersFound(retailers)
      } else if (retailers.length === 1) {
        // Single retailer found, set it directly
        setRetailer(retailers[0])
      }

      return {
        retailers,
        isMultiple: retailers.length > 1,
        orderLookupToken,
        message,
        statusCode,
      }
    } catch (error) {
      handleRetailerLookupError(error, { email, orderNumber })
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * Set the main retailer data
   * @param {Retailer} retailerData - Retailer object
   * @throws {Error} If retailer data is invalid
   * @public
   */
  function setRetailer(retailerData) {
    _validateRetailerData(retailerData)

    retailer.value = { ...retailerData }
    retailerId.value = retailerData.id
  }

  /**
   * Set multiple retailers found during lookup
   * @param {Retailer[]} retailers - Array of retailer objects
   * @throws {Error} If retailers is not an array
   * @public
   */
  function setRetailersFound(retailers) {
    _validateRetailersArray(retailers)

    retailersFound.value = retailers
    showRetailerSelection.value = true
  }

  /**
   * Select a retailer from the found retailers list
   * @param {Retailer} retailerData - Selected retailer object
   * @public
   */
  function selectRetailer(retailerData) {
    if (!retailerData || !retailerData.id) {
      selectedRetailer.value = null
      return
    }

    selectedRetailer.value = retailerData
    setRetailer(retailerData)
    showRetailerSelection.value = false
  }

  /**
   * Clear retailer selection state (for multiple retailers scenario)
   * @public
   */
  function clearSelection() {
    retailersFound.value = []
    selectedRetailer.value = null
    showRetailerSelection.value = false
  }

  /**
   * Reset retailer state
   * @param {Object} options - Reset options
   * @param {boolean} [options.keepSelection=false] - Keep the current retailer selection
   * @public
   */
  function reset({ keepSelection = false } = {}) {
    if (!keepSelection) {
      retailer.value = null
      retailerId.value = null
    }
    retailersFound.value = []
    selectedRetailer.value = null
    showRetailerSelection.value = false
    loading.value = false
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  return {
    // state
    retailer,
    retailerId,
    selectedRetailer,
    retailersFound,
    showRetailerSelection,
    loading,
    // computed
    hasMultipleRetailers,
    activeRetailer,
    // methods
    lookupByOrder,
    setRetailer,
    setRetailersFound,
    selectRetailer,
    clearSelection,
    reset,
  }
})
